import {useState} from 'react';
import {useLocation} from 'react-router-dom';

import {Button, DatePicker, SearchBar, Select} from '@/components';
import {Filter} from '@/components/Filter';
import {getCountryOptions} from '@/constants/formatters';
import {useCountryList} from '@/context/CountryListContext';
import type {AfriexVerificationStatus} from '@/types';
import {kycDisplayStatuses} from '@/types/AfriexVerificationStatus';
import {mapCountryListToOptions} from '@/utils/countries';

import type {KYCFiltersProps} from './types';

const KycFilters = ({
  filterCount,
  filtersToApply,
  currentFilters,
  clearFilters,
  selectAppliedFilters,
  handleApplyFilters,
  selectCurrentFilters,
  handleAutoComplete,
}: KYCFiltersProps) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status') as AfriexVerificationStatus;
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;

  const countryOptions = mapCountryListToOptions(countryList || []);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const fromDate = filtersToApply.fromDate
    ? new Date(filtersToApply.fromDate)
    : undefined;

  const toDate = filtersToApply.toDate
    ? new Date(filtersToApply.toDate)
    : undefined;

  const handleShowFilterChange = (): void => {
    setShowFilters(!showFilters);
  };

  const handleApplyFilter = (): void => {
    // Only close on success. Dismissing the panel after a rejected date range
    // hid the values the user has to correct. See hooks/useFilters.ts.
    if (handleApplyFilters()) {
      setShowFilters(false);
    }
  };

  const handleClearFilters = (): void => {
    setShowFilters(false);
    clearFilters();
  };
  return (
    <div className="mt-5 w-full">
      <div className="flex flex-col md:flex-col xl:flex-row xl:items-center justify-between gap-3">
        <div className="flex flex-col md:flex-col xl:flex-row gap-3 xl:items-center self-start md:self-center w-full flex-1">
          {/* Search Bar */}
          <SearchBar
            handleSearch={handleAutoComplete}
            handleAutoComplete={handleAutoComplete}
            clearFilters={clearFilters}
            inputClassName="w-full md:w-3/5 xl:w-3/5"
          />

          {/* Filter Box */}
          <div>
            <Filter
              closeFilter={() => setShowFilters(false)}
              handleShowFilter={handleShowFilterChange}
              label="Filters"
              bodyClassName="w-full sm:w-[400px]"
              positionLeft={1}
            >
              {/* Filter Body */}
              {showFilters && (
                <div className="p-2 space-y-4 max-w-4xl mx-auto w-full col-span-full">
                  <div>
                    <div className="flex flex-col md:flex-row gap-2">
                      <DatePicker
                        label="From Date"
                        selectedDate={fromDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters?.('fromDate', date)
                        }
                        placeholderText="Select Date"
                      />

                      <DatePicker
                        label="To Date"
                        selectedDate={toDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters?.('toDate', date)
                        }
                        placeholderText="Select Date"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={handleClearFilters}
                      colorScheme="cyan"
                      variant="ghost"
                    >
                      Clear
                    </Button>

                    <Button
                      onClick={handleApplyFilter}
                      colorScheme="cyan"
                      className="ml-3"
                    >
                      Apply Filters {filterCount && ` (${filterCount})`}
                    </Button>
                  </div>
                </div>
              )}
            </Filter>
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-2 justify-start xl:justify-end md:w-80">
          {/* Country Select */}
          <div className="w-3/4 md:w-full">
            <Select
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                selectCurrentFilters('country', e.target.value)
              }
              value={currentFilters.country}
              name="country"
              options={getCountryOptions(countryOptions)}
              placeholder="Filter by Country"
            />
          </div>

          {/* Status Select */}
          {!status && (
            <div className="w-3/4 md:w-full">
              <Select
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  selectCurrentFilters('status', e.target.value)
                }
                value={currentFilters.status}
                name="status"
                options={kycDisplayStatuses}
                placeholder="Filter by Status"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KycFilters;
